import { useCallback, useEffect, useState } from 'react';
import { Images, NswContractStage } from '@urbanx/agx-ui-components';
import { CoolingOffDetails, CoolingOffType } from '@urbanx/agx.campaigns.client';
import {
  AgxButton,
  AgxColumn,
  AgxModal,
  AgxRadioGroup,
  AgxRow,
  AgxTextInput,
  AgxTypography,
} from '@urbanx/agx.ui-components2';
import { useSelectedContract } from 'hooks';

interface AddCoolingOffPeriodProps {
  id: string;
  onConfirm: (coolingOffDetails: CoolingOffDetails) => void;
}

export function AddCoolingOffPeriod({ id, onConfirm }: AddCoolingOffPeriodProps) {
  const { contract } = useSelectedContract();
  const [validate, setValidate] = useState(false);
  const [showCoolingOffPeriodModal, setShowCoolingOffPeriodModal] = useState(false);
  const [selectedCoolingOffType, setSelectedCoolingOffType] = useState<CoolingOffType | undefined>(
    undefined
  );
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [selectedNDays, setSelectedNDays] = useState<number | undefined>(undefined);
  const [nDaysError, setNDaysError] = useState<string | undefined>(undefined);

  useEffect(() => {
    setValidate(false);
  }, [showCoolingOffPeriodModal]);

  useEffect(() => {
    setErrorMessage(undefined);
    setNDaysError(undefined);
    setSelectedCoolingOffType(undefined);
    setSelectedNDays(undefined);
  }, [showCoolingOffPeriodModal]);

  const handleConfirmClick = useCallback(() => {
    setValidate(true);
    if (!selectedCoolingOffType) {
      setErrorMessage('Select cooling off period');
      return;
    } else if (selectedCoolingOffType === CoolingOffType.NDays) {
      if (selectedNDays === undefined) {
        setNDaysError('Select number of days');
        return;
      } else if (selectedNDays <= 0) {
        setNDaysError('Number of days must be greater than 0');
        return;
      }
    }

    onConfirm({
      coolingOffType: selectedCoolingOffType,
      coolingOffDays: selectedCoolingOffType === CoolingOffType.NDays ? selectedNDays : undefined,
    });
  }, [onConfirm, selectedCoolingOffType, selectedNDays]);

  if (
    !contract?.contractStage ||
    ![NswContractStage.Accepted, NswContractStage.Unconditional].includes(
      contract.contractStage as NswContractStage
    )
  )
    return;

  return (
    <>
      <AgxButton
        id={`${id}-add-cooling-off-period`}
        variant="hollow"
        text="Add cooling off period"
        onClick={() => setShowCoolingOffPeriodModal(true)}
      />
      {showCoolingOffPeriodModal && (
        <AgxModal
          title="Cooling off period"
          type="single"
          variant="withDivider"
          actionButtonContainerStyle="withoutDivider"
          setIsDialogOpen={setShowCoolingOffPeriodModal}
          content={
            <AgxColumn
              gap="spacing-lg"
              fill
              padding={{ top: 'md', bottom: 'md', left: 'xl', right: 'xl' }}
            >
              <AgxRadioGroup
                id={`${id}-cooling-off-period-type`}
                layout="vertical"
                label="Cooling off period"
                options={[
                  {
                    label: '5 business days',
                    value: CoolingOffType.FiveDays,
                  },
                  {
                    label: '10 business days',
                    value: CoolingOffType.TenDays,
                  },
                  {
                    label: '_ business day(s)',
                    value: CoolingOffType.NDays,
                  },
                  {
                    label: 'No cooling off',
                    value: CoolingOffType.NoCoolingOff,
                  },
                ]}
                onChange={(value) => {
                  // Handle radio group change
                  setSelectedCoolingOffType(value as CoolingOffType);
                }}
                value={selectedCoolingOffType?.toString() || ''}
                subContent={
                  selectedCoolingOffType === CoolingOffType.NDays ? (
                    <>
                      <AgxRow gap="spacing-sm" padding="spacing-2xl">
                        <AgxTextInput
                          id={`${id}-cooling-off-ndays-dropdown`}
                          placeholder="__ days"
                          type="number"
                          hideOptionalText
                          maxLength={2}
                          noHeader
                          noDecimalPoints
                          defaultValue={selectedNDays?.toString() || ''}
                          onChange={(value) => {
                            const selectedValue = parseInt(value, 10);
                            setSelectedNDays(!isNaN(selectedValue) ? selectedValue : undefined);
                          }}
                          required
                        />
                        <AgxColumn padding={{ top: 'spacing-md' }}>
                          <AgxTypography
                            id={`${id}--cooling-off-ndays-text`}
                            type="body_ui"
                            size="medium"
                          >
                            day(s)
                          </AgxTypography>
                        </AgxColumn>
                      </AgxRow>
                      {validate && nDaysError && (
                        <AgxColumn padding={{ left: 'spacing-xl', bottom: 'spacing-xl' }}>
                          <div className={'errorMessage'}>
                            <Images.AlertCircle type="Danger" />
                            <AgxTypography id={`${id}-error`} type="form_error" size="small">
                              {nDaysError}
                            </AgxTypography>
                          </div>
                        </AgxColumn>
                      )}
                    </>
                  ) : undefined
                }
              />
              {validate && errorMessage && (
                <div className={'errorMessage'}>
                  <Images.AlertCircle type="Danger" />
                  <AgxTypography id={`${id}-error`} type="form_error" size="small">
                    {errorMessage}
                  </AgxTypography>
                </div>
              )}
            </AgxColumn>
          }
          actionButtons={
            <AgxRow gap="spacing-md" alignItems="flex-end" fill padding={{ top: 'spacing-2xl' }}>
              <AgxButton
                id={`${id}-cancel-cooling-off-period`}
                variant="hollow"
                text="Cancel"
                size="large"
                stretch
                onClick={() => {
                  setShowCoolingOffPeriodModal(false);
                }}
              />
              <AgxButton
                id={`${id}-confirm-cooling-off-period`}
                variant="primary"
                text="Confirm"
                size="large"
                stretch
                onClick={handleConfirmClick}
              />
            </AgxRow>
          }
        />
      )}
    </>
  );
}
